/**
 * Google Tasks Sync Service
 *
 * Thin facade delegating to focused sub-services.
 * Part of the Phase 4 service modularization.
 */

import { logger } from "@/lib/logger";
import type { IService, ServiceResponse } from "@/services/core/service-registry";
import type {
  GoogleTask,
  GoogleTaskList,
  GoogleTaskSyncMapping,
  SyncResult,
  CreateGoogleTaskRequest,
  UpdateGoogleTaskRequest,
  EnableListSyncRequest,
  ResolveConflictRequest,
} from "@/types/google-tasks";
import type { GoogleTasksAuthService } from "./google-tasks-auth.service";
import { GoogleTasksApiClient } from "./google-tasks-api-client.service";
import { GoogleTasksListService } from "./google-tasks-list.service";
import { GoogleTasksTaskService } from "./google-tasks-task.service";
import { GoogleTasksSyncEngine } from "./google-tasks-sync-engine.service";
import { GoogleTasksConflictService } from "./google-tasks-conflict.service";

const log = logger.createNamespace("GoogleTasksSyncService");

export interface IGoogleTasksSyncService extends IService {
  // Task List Operations
  getTaskLists(userId: string): Promise<ServiceResponse<GoogleTaskList[]>>;
  enableListSync(userId: string, request: EnableListSyncRequest): Promise<ServiceResponse<void>>;
  disableListSync(userId: string, listId: string): Promise<ServiceResponse<void>>;

  // Task Operations
  getTasks(userId: string, listId: string): Promise<ServiceResponse<GoogleTask[]>>;
  createTask(userId: string, listId: string, task: CreateGoogleTaskRequest): Promise<ServiceResponse<GoogleTask>>;
  updateTask(userId: string, listId: string, taskId: string, updates: UpdateGoogleTaskRequest): Promise<ServiceResponse<GoogleTask>>;
  completeTask(userId: string, listId: string, taskId: string): Promise<ServiceResponse<void>>;
  deleteTask(userId: string, listId: string, taskId: string): Promise<ServiceResponse<void>>;

  // Sync Operations
  syncFromGoogle(userId: string, listId?: string): Promise<ServiceResponse<SyncResult>>;
  syncToGoogle(userId: string, todoId?: string): Promise<ServiceResponse<SyncResult>>;
  syncBidirectional(userId: string): Promise<ServiceResponse<SyncResult>>;

  // Conflict Resolution
  getConflicts(userId: string): Promise<ServiceResponse<GoogleTaskSyncMapping[]>>;
  resolveConflict(userId: string, request: ResolveConflictRequest): Promise<ServiceResponse<void>>;

  setAuthService(authService: GoogleTasksAuthService): void;
}

export class GoogleTasksSyncService implements IGoogleTasksSyncService {
  public readonly name = "google-tasks-sync";
  private apiClient: GoogleTasksApiClient;
  private listService: GoogleTasksListService;
  private taskService: GoogleTasksTaskService;
  private syncEngine: GoogleTasksSyncEngine;
  private conflictService: GoogleTasksConflictService;

  constructor(
    services?: {
      apiClient?: GoogleTasksApiClient;
      listService?: GoogleTasksListService;
      taskService?: GoogleTasksTaskService;
      syncEngine?: GoogleTasksSyncEngine;
      conflictService?: GoogleTasksConflictService;
    }
  ) {
    this.apiClient = services?.apiClient ?? new GoogleTasksApiClient();
    this.listService = services?.listService ?? new GoogleTasksListService(this.apiClient);
    this.taskService = services?.taskService ?? new GoogleTasksTaskService(this.apiClient, this.listService);
    this.syncEngine = services?.syncEngine ?? new GoogleTasksSyncEngine(this.apiClient, this.listService, this.taskService);
    this.conflictService = services?.conflictService ?? new GoogleTasksConflictService(this.apiClient, this.listService, this.taskService, this.syncEngine);
  }

  async initialize(): Promise<void> {
    await this.apiClient.initialize();
    await this.listService.initialize();
    await this.taskService.initialize();
    await this.syncEngine.initialize();
    await this.conflictService.initialize();
    log.info("Google Tasks Sync Service facade initialized");
  }

  async dispose(): Promise<void> {
    await this.conflictService.dispose();
    await this.syncEngine.dispose();
    await this.taskService.dispose();
    await this.listService.dispose();
    await this.apiClient.dispose();
    log.info("Google Tasks Sync Service facade disposed");
  }

  setAuthService(authService: GoogleTasksAuthService): void {
    this.apiClient.setAuthService(authService);
  }

  getTaskLists(userId: string): Promise<ServiceResponse<GoogleTaskList[]>> {
    return this.listService.getTaskLists(userId);
  }

  enableListSync(userId: string, request: EnableListSyncRequest): Promise<ServiceResponse<void>> {
    return this.listService.enableListSync(userId, request);
  }

  disableListSync(userId: string, listId: string): Promise<ServiceResponse<void>> {
    return this.listService.disableListSync(userId, listId);
  }

  getTasks(userId: string, listId: string): Promise<ServiceResponse<GoogleTask[]>> {
    return this.taskService.getTasks(userId, listId);
  }

  createTask(userId: string, listId: string, task: CreateGoogleTaskRequest): Promise<ServiceResponse<GoogleTask>> {
    return this.taskService.createTask(userId, listId, task);
  }

  updateTask(userId: string, listId: string, taskId: string, updates: UpdateGoogleTaskRequest): Promise<ServiceResponse<GoogleTask>> {
    return this.taskService.updateTask(userId, listId, taskId, updates);
  }

  completeTask(userId: string, listId: string, taskId: string): Promise<ServiceResponse<void>> {
    return this.taskService.completeTask(userId, listId, taskId);
  }

  deleteTask(userId: string, listId: string, taskId: string): Promise<ServiceResponse<void>> {
    return this.taskService.deleteTask(userId, listId, taskId);
  }

  syncFromGoogle(userId: string, listId?: string): Promise<ServiceResponse<SyncResult>> {
    return this.syncEngine.syncFromGoogle(userId, listId);
  }

  syncToGoogle(userId: string, todoId?: string): Promise<ServiceResponse<SyncResult>> {
    return this.syncEngine.syncToGoogle(userId, todoId);
  }

  syncBidirectional(userId: string): Promise<ServiceResponse<SyncResult>> {
    return this.syncEngine.syncBidirectional(userId);
  }

  getConflicts(userId: string): Promise<ServiceResponse<GoogleTaskSyncMapping[]>> {
    return this.conflictService.getConflicts(userId);
  }

  resolveConflict(userId: string, request: ResolveConflictRequest): Promise<ServiceResponse<void>> {
    return this.conflictService.resolveConflict(userId, request);
  }
}

